import "katex/dist/katex.min.css";
import "@/styles/theme.css";
import "@/styles/global.css";
import "@/styles/markdown.css";
import "@/styles/v1.css";
import "@/styles/document.css";
import "@/styles/pfad.css";

import { mount } from "@/lib/dom";
import { router } from "@/app/router";
import { auth, topicsService, materialsService, subjectsService } from "@/services";
import { setFileRefResolver, setImageUrlHook } from "@/markdown/fileref";
import { thumbUrl } from "@/lib/pocketbase";
import { config } from "@/config";
import { offlineSync } from "@/services/offline/offline-sync.service";
import { requestPersistentStorage } from "@/services/document/localFiles";
import { startFileSync } from "@/services/document/fileSync";

import { AppLayout } from "@/components/layout/AppLayout";
import { LoginPage } from "@/pages/LoginPage";
import { HomePage } from "@/pages/HomePage";
import { ExamsPage } from "@/pages/ExamsPage";
import { HomeworkPage } from "@/pages/HomeworkPage";
import { TimetablePage } from "@/pages/TimetablePage";
import { TopicPage } from "@/pages/TopicPage";
import { LearnPage } from "@/pages/LearnPage";
import { PathPage } from "@/pages/PathPage";
import { LessonPage } from "@/pages/LessonPage";
import { CardsPage } from "@/pages/CardsPage";
import { ExamPage } from "@/pages/ExamPage";
import { SpeechPage } from "@/pages/SpeechPage";
import { GespraechPage } from "@/pages/GespraechPage";
import { ExamResultPage } from "@/pages/ExamResultPage";
import { StatsPage } from "@/pages/StatsPage";
import { SearchPage } from "@/pages/SearchPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { startLessonWatch } from "@/features/timetable/lessonWatch";
import { registerSW } from "virtual:pwa-register";

const appRoot = document.getElementById("app");
if (!appRoot) throw new Error("#app nicht gefunden");

// Neue Version im Hintergrund gefunden: NICHT sofort neuladen (die
// Standard-Registrierung würde das per `window.location.reload()` tun –
// reißt mitten aus einem Klick/einer Eingabe raus, mit einem hängenden
// Request gleichzeitig sah das wie "Klick friert ein, danach weiße Seite"
// aus). Stattdessen warten, bis wirklich nichts offen ist, und erst dann.
let updateReady = false;
function reloadWhenIdle(): void {
  if (!updateReady) return;
  const busy =
    document.querySelector(".modal-backdrop") ||
    document.activeElement?.matches("input, textarea, [contenteditable=true]");
  if (busy) return;
  window.location.reload();
}
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") reloadWhenIdle();
});
window.setInterval(reloadWhenIdle, 15_000);

registerSW({
  onNeedReload() {
    updateReady = true;
    reloadWhenIdle();
  },
});

// App-Name zentral aus der Config (Browser-Tab).
document.title = config.appName;

// Kurze Bild-/Datei-Links im Markdown (`pb:<id>/<datei>`) auflösen. Der
// Markdown-Layer kennt PocketBase nicht – er fragt hier nach.
setFileRefResolver((ref) => materialsService.urlForRef(ref));
setImageUrlHook(thumbUrl);

router
  .add("/login", LoginPage, { layout: "blank" })
  .add("/", HomePage, { requiresAuth: true })
  .add("/timetable", TimetablePage, { requiresAuth: true })
  .add("/homework", HomeworkPage, { requiresAuth: true })
  .add("/exams", ExamsPage, { requiresAuth: true })
  .add("/topic/:id", TopicPage, { requiresAuth: true })
  // Editor ist Full-Page (ohne Navbar/Sidebar) – eigene Kopfzeile in der Page.
  // Lazy wie das Arbeits-PDF: CodeMirror samt Editor-Werkzeugen soll nicht beim App-Start mitladen.
  .add(
    "/topic/:id/edit/:kind",
    (ctx) => import("@/pages/EditorPage").then((m) => m.EditorPage(ctx)),
    { requiresAuth: true, layout: "blank" }
  )
  // Arbeits-PDF: Full-Page ohne Navbar/Sidebar, UND per Lazy-Import registriert
  // (nicht wie die anderen Seiten oben statisch importiert) – zieht pdf-lib +
  // den kompletten pdf.js-Viewer (~600 kB) nach sich. Wer nie ein Dokument
  // öffnet, soll dafür nicht beim App-Start mitzahlen.
  .add(
    "/topic/:id/document",
    (ctx) => import("@/pages/DocumentPage").then((m) => m.DocumentPage(ctx)),
    { requiresAuth: true, layout: "blank" }
  )
  .add("/topic/:id/learn", LearnPage, { requiresAuth: true })
  .add("/topic/:id/learn/:method", LearnPage, { requiresAuth: true })
  // Lernpfad, Quiz, Karteikarten und Vortrag laufen als Vollbild-Inseln ohne
  // Navigationsleiste – eigenes Design, eigenes Scrolling (siehe styles/pfad.css).
  .add("/topic/:id/pfad", PathPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/pfad/:lesson", LessonPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/klausur", ExamPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/karten", CardsPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/karten/:mode", CardsPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/vortrag", SpeechPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/vortrag/:mode", SpeechPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/gespraech", GespraechPage, { requiresAuth: true, layout: "blank" })
  .add("/topic/:id/exam-result", ExamResultPage, { requiresAuth: true })
  .add("/stats", StatsPage, { requiresAuth: true })
  .add("/search", SearchPage, { requiresAuth: true })
  .setNotFound(NotFoundPage)
  .beforeEach((ctx, meta) => {
    const authed = auth.isAuthenticated();
    if (meta.requiresAuth && !authed) return "/login";
    if (ctx.path === "/login" && authed) return "/";
  })
  .onRender((view, ctx, meta) => {
    if (meta.layout === "blank") {
      mount(appRoot, view);
    } else {
      mount(appRoot, AppLayout(view, ctx));
    }
  })
  .start();

// Stundenplan-Wächter erst nach dem Router starten – er navigiert ggf. sofort
// zum Thema der laufenden Unterrichtsstunde.
startLessonWatch();

function whenIdle(delayMs: number, run: () => void): void {
  window.setTimeout(() => {
    if ("requestIdleCallback" in window) requestIdleCallback(run, { timeout: 10_000 });
    else run();
  }, delayMs);
}

// Einmalige, stille Rueckmigration der alten exams-Collection in die
// Themenfelder (siehe topicsService.migrateExamsToTopicFields).
if (auth.isAuthenticated()) void topicsService.migrateExamsToTopicFields();

// "Sonstiges" (Orga-Hausaufgaben ohne echtes Fach) einmalig anlegen, falls
// noch nicht vorhanden (siehe subjectsService.ensureSonstiges).
if (auth.isAuthenticated()) void subjectsService.ensureSonstiges();

// Offline-Ablage füllen/auffrischen (Listen, Notizen, Materiallisten) –
// nur wenn online, läuft still im Hintergrund (siehe offline-sync.service.ts).
if (auth.isAuthenticated() && navigator.onLine) {
  whenIdle(5_000, () => void offlineSync.refreshCache());
}

// Materialdateien liegen auf dem Gerät (localFiles.ts): der Browser soll sie
// nicht bei Platzmangel wegräumen, und Ausstehendes geht hoch, sobald Netz da ist.
if (auth.isAuthenticated()) {
  requestPersistentStorage();
  startFileSync();
}
